import { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { addToast } from "@/store/app/action.js";
import { PrimaryButton, DangerButton } from "@/components/Buttons.jsx";
import {
  getComplianceStates,
  updateComplianceState,
  getComplianceStateProducts,
  whitelistProduct,
  removeWhitelistedProduct,
} from "@/api/taxRules.js";
import CategoryRestrictionsModal from "./CategoryRestrictionsModal.jsx";
import { US_STATES } from "@/utils/usStates.js";

function StateRestrictions() {
  const dispatch = useDispatch();
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState("");

  // Drill-down state
  const [selectedState, setSelectedState] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productFilter, setProductFilter] = useState("all"); // all | whitelisted | not-whitelisted
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [togglingProduct, setTogglingProduct] = useState(null);

  // Category restrictions modal
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryModalState, setCategoryModalState] = useState(null);

  const loadStates = async () => {
    try {
      setLoading(true);
      const data = await getComplianceStates();
      setStates(data);
    } catch (error) {
      dispatch(addToast({ message: error.response?.data?.message || "Failed to load state compliance data", type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStates();
  }, []);

  const filteredStates = useMemo(() => {
    if (!stateFilter) return states;
    return states.filter((s) => s.code === stateFilter);
  }, [states, stateFilter]);

  const loadProducts = async (stateCode, page = 1, filter = "all") => {
    try {
      setProductsLoading(true);
      const params = { page };
      if (filter === "whitelisted") params.whitelisted = true;
      if (filter === "not-whitelisted") params.whitelisted = false;
      const data = await getComplianceStateProducts(stateCode, params);
      setProducts(data.products || data.data || []);
      setProductTotal(data.total || 0);
    } catch (error) {
      dispatch(addToast({ message: error.response?.data?.message || "Failed to load products", type: "error" }));
    } finally {
      setProductsLoading(false);
    }
  };

  const handleSelectState = (state) => {
    setSelectedState(state);
    setProductFilter("all");
    setProductPage(1);
    loadProducts(state.code, 1, "all");
  };

  const handleBackToStates = () => {
    setSelectedState(null);
    setProducts([]);
    setProductPage(1);
    setProductTotal(0);
  };

  const handleProductFilterChange = (filter) => {
    setProductFilter(filter);
    setProductPage(1);
    loadProducts(selectedState.code, 1, filter);
  };

  const handlePageChange = (newPage) => {
    setProductPage(newPage);
    loadProducts(selectedState.code, newPage, productFilter);
  };

  const handleToggleWhitelist = async (product) => {
    const skuId = product.sku_id || product._id;
    const wasWhitelisted = product.whitelisted;
    try {
      setTogglingProduct(skuId);
      if (wasWhitelisted) {
        await removeWhitelistedProduct(selectedState.code, skuId);
        dispatch(addToast({ message: "Product removed from whitelist", type: "success" }));
      } else {
        await whitelistProduct(selectedState.code, skuId);
        dispatch(addToast({ message: "Product whitelisted", type: "success" }));
      }
      // Optimistically update the local product state
      setProducts((prev) =>
        prev.map((p) =>
          (p.sku_id || p._id) === skuId
            ? { ...p, whitelisted: !wasWhitelisted }
            : p
        )
      );
      loadStates();
    } catch (error) {
      const msg = error.response?.status === 409
        ? "Product is already whitelisted"
        : error.response?.data?.message || "Failed to update product whitelist";
      dispatch(addToast({ message: msg, type: "error" }));
    } finally {
      setTogglingProduct(null);
    }
  };

  // ---------- Product drill-down view ----------
  if (selectedState) {
    return (
      <div>
        <button
          onClick={handleBackToStates}
          className="mb-4 text-sm text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to States
        </button>

        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
            {US_STATES.find((s) => s.code === selectedState.code)?.name || selectedState.code} — Products
          </h2>
          {selectedState.tobacco_compliance?.notes && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{selectedState.tobacco_compliance.notes}</p>
          )}
        </div>

        {/* Product filter */}
        <div className="mb-4 flex items-center gap-3">
          <select
            value={productFilter}
            onChange={(e) => handleProductFilterChange(e.target.value)}
            className="form-select text-sm"
          >
            <option value="all">All Products</option>
            <option value="whitelisted">Whitelisted Only</option>
            <option value="not-whitelisted">Not Whitelisted</option>
          </select>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {productTotal} product{productTotal !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Products table */}
        <div className="overflow-x-auto">
          <table className="table-auto w-full dark:text-slate-300">
            {productsLoading ? (
              <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                <tr>
                  <td colSpan="7" className="px-2 first:pl-5 last:pr-5 py-8">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2" />
                      <p className="text-sm text-slate-600 dark:text-slate-400">Loading products...</p>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <>
                <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Title</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Subtitle</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Category</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Volume</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">SKU</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Status</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-right">Actions</div>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                  {products.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-2 first:pl-5 last:pr-5 py-8 text-center text-slate-500">
                        No products found.
                      </td>
                    </tr>
                  ) : (
                    products.map((product) => {
                      const skuId = product.sku_id || product._id;
                      return (
                        <tr key={skuId}>
                          <td className="px-2 first:pl-5 last:pr-5 py-3">
                            <div className="font-medium text-slate-800 dark:text-slate-100">{product.name}</div>
                            {product.brand && (
                              <div className="text-xs text-slate-500 mt-0.5">{product.brand}</div>
                            )}
                          </td>
                          <td className="px-2 first:pl-5 last:pr-5 py-3">
                            <div className="text-slate-600 dark:text-slate-300">{product.subtitle || "—"}</div>
                          </td>
                          <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                            <div className="text-slate-600 dark:text-slate-300">{product.default_category?.name || "—"}</div>
                          </td>
                          <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                            <div className="text-slate-600 dark:text-slate-300">{product.volume || "—"}</div>
                          </td>
                          <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                            <div className="text-slate-600 dark:text-slate-300">{skuId}</div>
                          </td>
                          <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                              product.whitelisted
                                ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400"
                                : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                            }`}>
                              {product.whitelisted ? "Whitelisted" : "Not Whitelisted"}
                            </span>
                          </td>
                          <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap text-right">
                            {product.whitelisted ? (
                              <DangerButton
                                size="xs"
                                onClick={() => handleToggleWhitelist(product)}
                                loading={togglingProduct === skuId}
                                disabled={togglingProduct === skuId}
                              >
                                Remove
                              </DangerButton>
                            ) : (
                              <PrimaryButton
                                size="xs"
                                onClick={() => handleToggleWhitelist(product)}
                                loading={togglingProduct === skuId}
                                disabled={togglingProduct === skuId}
                              >
                                Whitelist
                              </PrimaryButton>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </>
            )}
          </table>
        </div>

        {/* Pagination */}
        {productTotal > 0 && !productsLoading && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-slate-500 dark:text-slate-400">
              Page {productPage}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(productPage - 1)}
                disabled={productPage <= 1}
                className="btn-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-slate-300"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(productPage + 1)}
                disabled={products.length === 0}
                className="btn-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-slate-300"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------- States list view ----------
  return (
    <div>
      {/* State filter dropdown */}
      <div className="mb-4">
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="form-select w-full md:w-64"
        >
          <option value="">All States</option>
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* States table */}
      <div className="overflow-x-auto">
        <table className="table-auto w-full dark:text-slate-300">
          {loading ? (
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              <tr>
                <td colSpan="7" className="px-2 first:pl-5 last:pr-5 py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 dark:text-slate-400">Loading state restrictions...</p>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <>
              <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">State</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Whitelist</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Source</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Matched / Total</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Restricted Categories</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Last Synced</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Notes</div>
                  </th>
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                {filteredStates.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-2 first:pl-5 last:pr-5 py-8 text-center text-slate-500">
                      No state restriction data found.
                    </td>
                  </tr>
                ) : (
                  filteredStates.map((state) => {
                    const tc = state.tobacco_compliance || {};
                    const stateName = US_STATES.find((s) => s.code === state.code)?.name || state.code;
                    return (
                      <tr
                        key={state.code}
                        onClick={() => handleSelectState(state)}
                        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50"
                      >
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className="font-medium text-indigo-500 dark:text-indigo-400">
                            {stateName} ({state.code})
                          </div>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                            tc.whitelist_enabled
                              ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400"
                              : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                          }`}>
                            {tc.whitelist_enabled ? "Enabled" : "Disabled"}
                          </span>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className="text-left">{tc.whitelist_source || "—"}</div>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className="text-left">
                            {tc.matched_count ?? 0} / {tc.total_whitelist ?? 0}
                          </div>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCategoryModalState(state);
                              setCategoryModalOpen(true);
                            }}
                            className="text-left text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 underline-offset-2 hover:underline"
                          >
                            {state.categories_restricted?.length ?? 0}
                          </button>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                          <div className="text-left">
                            {tc.last_synced ? new Date(tc.last_synced).toLocaleDateString() : "—"}
                          </div>
                        </td>
                        <td className="px-2 first:pl-5 last:pr-5 py-3">
                          <div className="text-left text-slate-500 dark:text-slate-400 truncate max-w-xs">
                            {tc.notes || "—"}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </>
          )}
        </table>
      </div>

      <CategoryRestrictionsModal
        isOpen={categoryModalOpen}
        setIsOpen={setCategoryModalOpen}
        state={categoryModalState}
        onSave={loadStates}
      />
    </div>
  );
}

export default StateRestrictions;
